<template>
    <div :class="css.modules">
        <renderHeader />
        <renderBody />
    </div>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiIcon } from "various-ui";
//* 公共组件
import { Favorites } from "@/components/controls";
//* 组件属性
import { type ModulesNavigation, ModulesPropsOption } from "./src";
import { useComposable } from "./src/composable";
//* 样式
import css from "./style/index.module.less";
//* 插件
const i18n = useI18n();
//* 组件属性声明
const define = defineProps(ModulesPropsOption);
const slots = defineSlots();
//* 组合属性
const { refs, nodes, methods, computeds } = useComposable(define);
const { navigations, navigationIsLeft, navigationIsRight } = computeds;

//* 组件挂载时, 注册观察者与滚动事件
onMounted(() => methods.init());
//* 组件卸载时, 注销滚动事件
onBeforeUnmount(() => {
    window.removeEventListener("scroll", methods.scrollNavigationHandler);
});

//* 渲染函数（内容）
const renderBody = defineComponent({
    render() {
        return <div class={css.body}>{slots.default?.()}</div>;
    },
});

//* 渲染函数（头部）
const renderHeader = defineComponent({
    render() {
        //* 渲染函数（头部内容）
        const renderContent = () => {
            //* 渲染函数（中文名字）
            const renderChineseName = () => {
                if (define.data.name_zh && i18n.locale.value == "zh") {
                    return <div class={`${css.name_zh}`} innerHTML={define.data.name_zh}></div>;
                }
            };

            //* 渲染函数（别名）
            const renderNicknames = () => {
                if (define.data.nicknames.length) {
                    return (
                        <div class={css.nicknames}>
                            <div>{`${i18n.t("Alias")}:`}</div>
                            <div class={css["nicknames-container"]}>
                                {define.data.nicknames.map((nickname) => (
                                    <div class={css.nickname} innerHTML={nickname}></div>
                                ))}
                            </div>
                        </div>
                    );
                }
            };

            //* 渲染函数（名字）
            const renderName = () => {
                return (
                    <div class={[css.title, define.data.nicknames.length || define.data.name_zh ? css.mb12 : ""]}>
                        <div class={css.type} v-show={define.data.type && define.mode == "Gene"} innerHTML={define.data.type}></div>
                        {define.data.cyagen && define.mode != "Model" ? (
                            <div class={`${css.name} font-bold font-variable`}>
                                <a href={`${define.data.cyagen}`} target="_blank" innerHTML={define.data.name}></a>
                            </div>
                        ) : (
                            <div class={`${css.name} font-bold font-variable`} innerHTML={define.data.name}></div>
                        )}
                    </div>
                );
            };

            return (
                <div class={css["header-container"]} style={{ backgroundImage: `url(${define.cover})` }}>
                    {define.data.cyagen ? (
                        <a
                            class={[`${css.container} ${css["model-container"]} container`, !define.data.product && css.active]}
                            ref={nodes.container}
                            href={define.data.cyagen}
                            target="_blank">
                            <div class={css.names}>
                                {renderName()}
                                {renderChineseName()}
                                {renderNicknames()}
                            </div>
                            <div class={css.cyagen}>
                                <img class={css.image} src="https://rdgta.cdn.cyagen.com/rddc/images/cyagen3.png" alt="cyagen" />
                                <div class={css.context}>
                                    <span>{define.data.product ? i18n.t("WantToBuyThisModel") : i18n.t("NeedModelConstructionServices")}</span>
                                    <br />
                                    <span class={css.underline}>{i18n.t("ClickHere")} &gt;&gt;</span>
                                </div>
                            </div>
                            <div class="ml-[24px] self-start" onClick={(e) => e.preventDefault()}>
                                <Favorites size="small" />
                            </div>
                        </a>
                    ) : (
                        <div class={`${css.container} container`} ref={nodes.container}>
                            <div class={css.names}>
                                {renderName()}
                                {renderChineseName()}
                                {renderNicknames()}
                            </div>
                            <Favorites />
                        </div>
                    )}
                </div>
            );
        };

        //* 渲染函数（导航列表）
        const renderNavigation = () => {
            //* 检测是否显示导航
            if (!navigations.value.length) return;
            //* 渲染函数（导航）
            const renderNavigation = (data: ModulesNavigation, index: number) => {
                const className = refs.navigationKey.value == index ? `${css.navigation} ${css.active}` : css.navigation;
                return (
                    <div class={className} onClick={() => methods.openNavigation(data.ref)}>
                        {data.name}
                    </div>
                );
            };

            return (
                <div class={`${css["header-navigations"]} ${css.container}`}>
                    <div class={`${css["navigations-container"]} container`}>
                        <div class={css.content} ref={nodes.navigations}>
                            {navigations.value.filter((v) => v.visible).map((v, i) => renderNavigation(v, i))}
                        </div>
                        <div class={css.control} v-show={navigationIsLeft.value} onClick={methods.openNavigationLeft}>
                            <UiIcon name="arrow"></UiIcon>
                        </div>
                        <div class={`${css.control} ${css.active}`} v-show={navigationIsRight.value} onClick={methods.openNavigationRight}>
                            <UiIcon name="arrow"></UiIcon>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <div class={css.header} ref={nodes.header}>
                {renderContent()}
                {renderNavigation()}
            </div>
        );
    },
});
</script>
